const isRecord = (value) => typeof value === 'object' && value !== null && !Array.isArray(value);
const isDashboardFilterMap = (value) => {
    if (!isRecord(value)) {
        return false;
    }
    return Object.values(value).every((entry) => typeof entry === 'string');
};
/**
 * Normalises a route path for sessionStorage keys (leading slash, no trailing slash).
 */
export const normalizeDashboardPath = (path) => {
    const trimmed = path.trim();
    if (trimmed === '' || trimmed === '/') {
        return '/';
    }
    const withLeadingSlash = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
    return withLeadingSlash.endsWith('/')
        ? withLeadingSlash.slice(0, -1)
        : withLeadingSlash;
};
/**
 * sessionStorage key for the last filter query on a dashboard path.
 * Example: dashboardQuery:/all-documents
 */
export const dashboardQueryStorageKey = (path) => `dashboardQuery:${normalizeDashboardPath(path)}`;
const readQueryParam = (value) => {
    if (value === undefined || value === null) {
        return undefined;
    }
    if (Array.isArray(value)) {
        const first = value[0];
        if (first === undefined || first === null || first === '') {
            return undefined;
        }
        return first;
    }
    if (value === '') {
        return undefined;
    }
    return value;
};
/**
 * True when the route query includes at least one known filter key.
 */
export const queryHasDashboardFilters = (query, defaults) => Object.keys(defaults).some((key) => readQueryParam(query[key]) !== undefined);
/**
 * Builds a query object from current filters, omitting keys that match defaults.
 * Does not include pagination (`page`).
 */
export const serializeDashboardFilters = (filters, defaults) => {
    const serialized = {};
    for (const key of Object.keys(filters)) {
        const value = filters[key];
        if (value === undefined || value === '') {
            continue;
        }
        if (Object.hasOwn(defaults, key) && defaults[key] === value) {
            continue;
        }
        serialized[key] = value;
    }
    return serialized;
};
/**
 * Merges known filter keys from a route query onto defaults.
 * Unknown query keys are ignored.
 */
export const filtersFromQuery = (query, defaults) => {
    const filters = { ...defaults };
    for (const key of Object.keys(defaults)) {
        const value = readQueryParam(query[key]);
        if (value !== undefined) {
            filters[key] = value;
        }
    }
    return filters;
};
/**
 * Merges a stored filter map onto defaults (only known keys).
 */
export const mergeFiltersWithDefaults = (stored, defaults) => {
    const filters = { ...defaults };
    for (const key of Object.keys(defaults)) {
        if (Object.hasOwn(stored, key) && stored[key] !== '') {
            filters[key] = stored[key];
        }
    }
    return filters;
};
export const readSessionDashboardFilters = (path) => {
    const raw = sessionStorage.getItem(dashboardQueryStorageKey(path));
    if (raw === null || raw === '') {
        return null;
    }
    try {
        const parsed = JSON.parse(raw);
        if (!isDashboardFilterMap(parsed)) {
            return null;
        }
        if (Object.keys(parsed).length === 0) {
            return null;
        }
        return parsed;
    }
    catch {
        return null;
    }
};
/**
 * Persists the last non-default filter query. Clears the key when filters are empty
 * (user reset to defaults).
 */
export const writeSessionDashboardFilters = (path, filters) => {
    const key = dashboardQueryStorageKey(path);
    if (Object.keys(filters).length === 0) {
        sessionStorage.removeItem(key);
        return;
    }
    sessionStorage.setItem(key, JSON.stringify(filters));
};
export const clearSessionDashboardFilters = (path) => {
    sessionStorage.removeItem(dashboardQueryStorageKey(path));
};
/**
 * Sidebar navigation must reset filters even when the destination URL already
 * has an empty query (defaults omit query keys). Empty query alone cannot mean
 * both "Browser Back with defaults" and "sidebar reset".
 */
let pendingSidebarDashboardResetPath = null;
export const markSidebarDashboardReset = (path) => {
    pendingSidebarDashboardResetPath = normalizeDashboardPath(path);
};
export const peekSidebarDashboardReset = (path) => pendingSidebarDashboardResetPath === normalizeDashboardPath(path);
/**
 * Returns true once for a pending sidebar reset to `path`, then clears it.
 */
export const consumeSidebarDashboardReset = (path) => {
    if (pendingSidebarDashboardResetPath !== normalizeDashboardPath(path)) {
        return false;
    }
    pendingSidebarDashboardResetPath = null;
    return true;
};
/** Test helper — clears any pending sidebar reset signal. */
export const clearPendingSidebarDashboardReset = () => {
    pendingSidebarDashboardResetPath = null;
};
/**
 * On mount: URL filter query wins; empty filter query → session; neither → defaults.
 * Keep-alive Back should not call this (component is not remounted).
 */
export const hydrateDashboardFilters = (path, query, defaults) => {
    if (queryHasDashboardFilters(query, defaults)) {
        return {
            filters: filtersFromQuery(query, defaults),
            source: 'url',
        };
    }
    const sessionFilters = readSessionDashboardFilters(path);
    if (sessionFilters !== null) {
        return {
            filters: mergeFiltersWithDefaults(sessionFilters, defaults),
            source: 'session',
        };
    }
    return {
        filters: { ...defaults },
        source: 'defaults',
    };
};
const serializedQueriesEqual = (left, right, filterKeys) => {
    for (const key of filterKeys) {
        const leftValue = left[key];
        const rightValue = readQueryParam(right[key]);
        if ((leftValue ?? undefined) !== rightValue) {
            return false;
        }
    }
    return true;
};
/**
 * Writes filters to the URL with router.replace (does not push history) and
 * updates sessionStorage. Omits default values from the query.
 */
export const replaceDashboardQuery = async (router, path, filters, defaults, currentQuery) => {
    const serialized = serializeDashboardFilters(filters, defaults);
    writeSessionDashboardFilters(path, serialized);
    const filterKeys = Object.keys(defaults);
    if (currentQuery !== undefined &&
        serializedQueriesEqual(serialized, currentQuery, filterKeys)) {
        return;
    }
    await router.replace({
        path: normalizeDashboardPath(path),
        query: serialized,
    });
};
/**
 * Hydrates filters and, when restoring from session, replaces the URL so the
 * address bar matches the restored filters.
 */
export const resolveDashboardFilters = async (router, path, query, defaults) => {
    const hydrated = hydrateDashboardFilters(path, query, defaults);
    if (hydrated.source === 'session') {
        await replaceDashboardQuery(router, path, hydrated.filters, defaults, query);
    }
    return hydrated;
};
